/**
 * The driver's offline outbox (docs/mobile-design.md Phase 3, docs/offline.md §3): start, arrive and complete (with
 * the proof-of-delivery photos and signature) are saved on the phone first, shown straight away, and sent in order
 * when there is signal; so are a vehicle check or defect report made with no signal (chunk 2). Every op carries an Idempotency-Key (its id; each photo upload has its own key) so a resend
 * after a lost answer runs once on the server. Platform-neutral: storage is the shared outbox store (unsent.ts), the
 * app supplies the online check and wires the cache update (onSent). Network/5xx/429/408 retry with backoff; a 409
 * whose effect is already there ("Stop already done") counts as sent; other 4xx/409 wait for the driver (Retry/Discard).
 */
import {
  ApiError, classifyOutcome, isAlreadyDoneConflict, isInFlightConflict, outboxRetryDelayMs, type CheckResult, type FleetReportKind, type PendingRunOp, type ReportResult, type RunOpKind, type RunView,
} from '@tradeoutlet/shared';
import { dataApi } from './client';
import { createOutboxStore, type KeyValueStore, type OutboxStore } from './unsent';

export const RUN_OP_KINDS: readonly RunOpKind[] = ['run.start', 'run.arrive', 'run.complete', 'fleet.check', 'fleet.report'];
/** Phase 3 chunk 2: a vehicle check or defect report saved with no signal rides the same outbox (keyed per vehicle). */
export const isFleetOp = (k: RunOpKind) => k === 'fleet.check' || k === 'fleet.report';
export interface FleetCheckPayload { vehicle_id: string; journey_id: string | null; odometer_miles: number | null; items: { key: string; ok: boolean; note: string | null }[] }
export interface FleetReportPayload { vehicle_id: string; journey_id: string | null; kind: FleetReportKind; severity: 'low' | 'medium' | 'high'; description: string; off_road: boolean; lat: number | null; lng: number | null }

export interface RunOutboxFile { key: string; data_url: string; upload_id?: string | null }
export interface RunOutboxOp {
  id: string; // = Idempotency-Key
  kind: RunOpKind;
  userId: string;
  workspaceId: string;
  /** The run (run ops), or `vehicle:<id>` for a saved check / report: ops with the same key send strictly in order. */
  journeyId: string;
  stopId?: string | null;
  /** What the driver sees in the waiting list, e.g. "Delivered · Leeds store". */
  label?: string;
  createdAt: string;
  attempts: number;
  state: 'queued' | 'sending' | 'failed' | 'conflict';
  /** Epoch ms before which a retry waits (backoff). */
  nextAt?: number;
  error?: string;
  payload: {
    tracking_consent?: boolean;
    pod?: { pod_name: string | null; pod_pallets: number | null; pod_note: string | null; failed: boolean };
    signature?: RunOutboxFile | null;
    photos?: RunOutboxFile[];
    check?: FleetCheckPayload;
    report?: FleetReportPayload;
  };
}

/** `id` may be given when a direct send with that Idempotency-Key lost its answer, so the resend can't run twice. */
export type NewRunOp = Pick<RunOutboxOp, 'kind' | 'workspaceId' | 'journeyId' | 'stopId' | 'label' | 'payload'> & { id?: string };
/** What a sent check or report answered (the issue it raised). */
export type FleetOpResult = CheckResult | ReportResult;

export interface RunOutboxOptions {
  store: OutboxStore<RunOutboxOp>;
  uuid: () => string;
  isOnline?: () => boolean;
  now?: () => number;
  rand?: () => number;
  /** A sent op's answer (the run as the server now has it), or null when the server already had it (refetch). */
  onSent?: (op: RunOutboxOp, view: RunView | null, result?: FleetOpResult | null) => void;
}

export interface RunOutbox {
  readonly userId: string | null;
  load(userId: string): Promise<void>;
  ops(): RunOutboxOp[];
  subscribe(fn: () => void): () => void;
  enqueue(op: NewRunOp): Promise<RunOutboxOp>;
  flush(): Promise<void>;
  retry(id: string): Promise<void>;
  discard(id: string): Promise<void>;
  /** A fresh Idempotency-Key (the app's uuid). */
  newKey(): string;
  /** Sign-out: forget the in-memory queue (the store itself is wiped by the app's sign-out). */
  forget(): void;
}

/** What the run screen overlays (shared/mobile/runs applyPendingRunOps). */
export function toPendingRunOp(o: RunOutboxOp): PendingRunOp {
  return {
    id: o.id, kind: o.kind, journeyId: o.journeyId, stopId: o.stopId ?? null, createdAt: o.createdAt,
    failed: o.payload.pod?.failed, pod_name: o.payload.pod?.pod_name ?? null, pod_note: o.payload.pod?.pod_note ?? null, pod_pallets: o.payload.pod?.pod_pallets ?? null,
    photos: o.payload.photos?.length ?? 0, signed: Boolean(o.payload.signature), tracking_consent: o.payload.tracking_consent,
  };
}

export const runOutboxStore = (open: (userId: string) => KeyValueStore<RunOutboxOp>) => createOutboxStore<RunOutboxOp>(open, { kinds: RUN_OP_KINDS });

type Outcome = { kind: 'sent'; view: RunView | null; result?: FleetOpResult | null } | { kind: 'retry'; error: string; delay?: number } | { kind: 'failed' | 'conflict'; error: string };

function outcomeOf(e: unknown): Outcome {
  const status = e instanceof ApiError ? e.status : null;
  const msg = e instanceof Error ? e.message : String(e);
  const detail = e instanceof ApiError ? `${msg} ${JSON.stringify(e.body ?? '')}` : msg;
  const c = classifyOutcome(status);
  if (c === 'conflict') {
    if (isInFlightConflict(detail)) return { kind: 'retry', error: 'Still sending', delay: 2000 };
    if (isAlreadyDoneConflict(detail)) return { kind: 'sent', view: null };
    return { kind: 'conflict', error: msg };
  }
  if (c === 'retry') return { kind: 'retry', error: status == null ? 'No signal' : msg };
  return { kind: c === 'sent' ? 'failed' : c, error: msg };
}

export function createRunOutbox(o: RunOutboxOptions): RunOutbox {
  const now = o.now ?? Date.now;
  const online = o.isOnline ?? (() => true);
  let userId: string | null = null;
  let ops: RunOutboxOp[] = [];
  let snapshot: RunOutboxOp[] = [];
  const listeners = new Set<() => void>();
  let flushing: Promise<void> | null = null;
  let again = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const emit = () => { snapshot = ops.map(x => ({ ...x })); listeners.forEach(f => f()); };
  const save = async (op: RunOutboxOp) => { await o.store.save(op); };
  const drop = async (op: RunOutboxOp) => { ops = ops.filter(x => x.id !== op.id); await o.store.remove(op.userId, op.id); };

  const schedule = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    const waits = ops.filter(x => x.state === 'queued' && x.nextAt && x.nextAt > now()).map(x => x.nextAt!);
    if (!waits.length) return;
    timer = setTimeout(() => { timer = null; void api.flush(); }, Math.max(50, Math.min(...waits) - now()));
  };

  async function send(op: RunOutboxOp): Promise<Outcome> {
    const api = dataApi().fleet;
    const ref = { ws: op.workspaceId, id: op.journeyId };
    try {
      if (isFleetOp(op.kind)) {
        // a saved check / report: its photos first (each with its own key), then the check or report itself.
        for (const f of op.payload.photos ?? []) {
          if (f.upload_id) continue;
          f.upload_id = (await api.upload(op.workspaceId, f.data_url, f.key)).id;
          await save(op);
        }
        const photo_upload_ids = (op.payload.photos ?? []).map(p => p.upload_id!).filter(Boolean);
        const result = op.kind === 'fleet.check'
          ? await api.submitCheck(op.workspaceId, { ...op.payload.check!, photo_upload_ids }, op.id)
          : await api.report(op.workspaceId, { ...op.payload.report!, photo_upload_ids }, op.id);
        return { kind: 'sent', view: null, result };
      }
      if (op.kind === 'run.start') return { kind: 'sent', view: await api.start(ref, Boolean(op.payload.tracking_consent), op.id) };
      if (op.kind === 'run.arrive') return { kind: 'sent', view: await api.arrive(ref, op.stopId!, op.id) };
      // run.complete: uploads first (each with its own key, so a resend reuses the same upload), then the stop.
      const files = [...(op.payload.signature ? [op.payload.signature] : []), ...(op.payload.photos ?? [])];
      for (const f of files) {
        if (f.upload_id) continue;
        const up = await api.runUpload(ref, f.data_url, f.key);
        f.upload_id = up.id;
        await save(op);
      }
      const pod = op.payload.pod ?? { pod_name: null, pod_pallets: null, pod_note: null, failed: false };
      const view = await api.complete(ref, op.stopId!, {
        ...pod, signature_upload_id: op.payload.signature?.upload_id ?? null, photo_upload_ids: (op.payload.photos ?? []).map(p => p.upload_id!).filter(Boolean),
      }, op.id);
      return { kind: 'sent', view };
    } catch (e) {
      return outcomeOf(e);
    }
  }

  async function runFlush() {
    if (!online()) return;
    const blocked = new Set<string>();
    for (const op of [...ops].sort((a, b) => a.createdAt.localeCompare(b.createdAt))) {
      if (blocked.has(op.journeyId)) continue;
      if (op.state === 'failed' || op.state === 'conflict' || (op.nextAt && op.nextAt > now())) { blocked.add(op.journeyId); continue; }
      op.state = 'sending'; emit();
      const r = await send(op);
      if (r.kind === 'sent') { await drop(op); o.onSent?.(op, r.view, r.result ?? null); emit(); continue; }
      blocked.add(op.journeyId); // keep each run's actions in order
      op.attempts += 1; op.error = r.error;
      if (r.kind === 'retry') { op.state = 'queued'; op.nextAt = now() + (r.delay ?? outboxRetryDelayMs(op.attempts, (o.rand ?? Math.random)())); }
      else { op.state = r.kind; op.nextAt = undefined; }
      await save(op); emit();
      if (r.kind === 'retry' && r.error === 'No signal') break; // nothing else will get through either
    }
  }

  const api: RunOutbox = {
    get userId() { return userId; },
    async load(u) {
      if (userId === u && ops.length) return;
      userId = u;
      ops = (await o.store.load(u, now())).map(x => (x.state === 'sending' ? { ...x, state: 'queued' as const } : x));
      emit();
      void api.flush();
    },
    ops: () => snapshot,
    subscribe(fn) { listeners.add(fn); return () => { listeners.delete(fn); }; },
    async enqueue(n) {
      if (!userId) throw new Error('Run outbox: no user loaded');
      const file = (f: RunOutboxFile): RunOutboxFile => ({ ...f, key: f.key || o.uuid() });
      const payload = { ...n.payload, signature: n.payload.signature ? file(n.payload.signature) : n.payload.signature, photos: n.payload.photos?.map(file) };
      const op: RunOutboxOp = { ...n, payload, id: n.id || o.uuid(), userId, createdAt: new Date(now()).toISOString(), attempts: 0, state: 'queued' };
      ops = [...ops, op];
      await save(op); emit();
      void api.flush();
      return op;
    },
    flush() {
      if (flushing) { again = true; return flushing; }
      flushing = (async () => {
        try { do { again = false; await runFlush(); } while (again); }
        finally { flushing = null; schedule(); }
      })();
      return flushing;
    },
    async retry(id) {
      const op = ops.find(x => x.id === id);
      if (!op) return;
      op.state = 'queued'; op.nextAt = undefined; op.error = undefined;
      // a nudge also clears backoff on the same run's waiting ops
      for (const x of ops) if (x.journeyId === op.journeyId && x.state === 'queued') x.nextAt = undefined;
      await save(op); emit();
      await api.flush();
    },
    async discard(id) {
      const op = ops.find(x => x.id === id);
      if (!op) return;
      await drop(op); emit();
      o.onSent?.(op, null);
      void api.flush();
    },
    newKey: () => o.uuid(),
    forget() { if (timer) clearTimeout(timer); timer = null; ops = []; userId = null; emit(); },
  };
  return api;
}
